import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import { MapTrifold, Truck, ClipboardText, Scales, Lightning, Bell, Gauge, ClockCounterClockwise, SignOut, SteeringWheel, Gear, Crown, CreditCard, Path, Wrench, ThumbsUp, Receipt, ClipboardText as ClipboardCheck, WarningCircle, Heartbeat } from "@phosphor-icons/react";
import { dashboardApi, billingApi, http } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useKpis } from "@/hooks/useKpis";
import PushToggle from "@/components/pwa/PushToggle";
import SettingsDialog from "@/components/settings/SettingsDialog";
import CompanySwitcher from "@/components/layout/CompanySwitcher";

const NAV_ITEMS_BASE = [
  { to: "/live", label: "Live Map", icon: MapTrifold, testId: "nav-live" },
  { to: "/history", label: "History", icon: ClockCounterClockwise, testId: "nav-history" },
  { to: "/assets", label: "Assets", icon: Truck, testId: "nav-assets" },
  { to: "/jobs", label: "Dispatch", icon: ClipboardText, testId: "nav-jobs" },
  { to: "/routes", label: "Routes", icon: Path, testId: "nav-routes" },
  { to: "/maintenance", label: "Service", icon: Wrench, testId: "nav-maintenance" },
  { to: "/compliance", label: "Compliance", icon: Scales, testId: "nav-compliance" },
  { to: "/expirations", label: "Expirations", icon: WarningCircle, testId: "nav-expirations" },
  { to: "/dvir", label: "DVIR", icon: ClipboardCheck, testId: "nav-dvir" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices" },
  { to: "/feedback", label: "Feedback", icon: ThumbsUp, testId: "nav-feedback" },
  { to: "/billing", label: "Billing", icon: CreditCard, testId: "nav-billing" },
];

export default function DashboardLayout() {
  const { kpis, setKpis } = useKpis({
    active_assets: 0,
    alert_assets: 0,
    scheduled_jobs: 0,
    compliance_alerts: 0,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [fleetioConnected, setFleetioConnected] = useState(false);

  // Initial fetch + safety-net poll every 30s if WS happens to be down.
  useEffect(() => {
    const load = async () => {
      try {
        const data = await dashboardApi.kpis();
        setKpis(data);
      } catch (e) {
        console.error(e);
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [setKpis]);

  useEffect(() => {
    billingApi.status().then(setBilling).catch(() => setBilling(null));
  }, [location.pathname]);

  useEffect(() => {
    http.get("/fleetio/status").then((r) => setFleetioConnected(!!r.data?.is_connected)).catch(() => setFleetioConnected(false));
  }, [user?.company_id, user?.company_name]);

  const NAV_ITEMS = [
    ...NAV_ITEMS_BASE,
    ...(fleetioConnected ? [{ to: "/fleet-health", label: "Fleet Health", icon: Heartbeat, testId: "nav-fleet-health" }] : []),
    ...(user?.role === "super_admin" ? [{ to: "/super", label: "Super-Admin", icon: Crown, testId: "nav-super" }] : []),
  ];

  const pageTitle = NAV_ITEMS.find((n) => location.pathname.startsWith(n.to))?.label || "Dashboard";

  return (
    <div className="min-h-screen flex bg-[#111111] text-white" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className="w-[88px] lg:w-[240px] border-r border-[#2a2a2a] bg-[#0d0d0d] flex flex-col">
        {/* Logo */}
        <div className="h-[72px] border-b border-[#2a2a2a] flex items-center px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#ff8c00] flex items-center justify-center" data-testid="brand-logo">
              <Lightning size={22} weight="fill" color="#000" />
            </div>
            <div className="hidden lg:block">
              <div className="font-heading font-black text-base tracking-tight leading-none">LOAD LOGIC</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mt-1">Dispatch OS</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 lg:px-3 space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 transition-colors duration-150 group ${
                  isActive
                    ? "bg-[#ff8c00] text-black"
                    : "text-[#a1a1aa] hover:bg-[#1a1a1a] hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={20} weight={isActive ? "fill" : "regular"} />
                  <span className="hidden lg:inline text-sm font-medium uppercase tracking-wider">
                    {item.label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-[#2a2a2a] p-3 lg:p-4 space-y-3">
          <Link
            to="/driver"
            className="flex items-center justify-center gap-2 py-2 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
            data-testid="driver-mode-link"
          >
            <SteeringWheel size={14} weight="bold" />
            <span className="hidden lg:inline">Driver Mode</span>
          </Link>
          <div className="hidden lg:block">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-2">System Status</div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#22c55e] rounded-full ll-status-blink"></span>
              <span className="text-xs font-mono text-[#a1a1aa]">ONLINE · 99.98%</span>
            </div>
          </div>
          <div className="lg:hidden flex justify-center">
            <span className="w-2 h-2 bg-[#22c55e] rounded-full ll-status-blink"></span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="h-[72px] border-b border-[#2a2a2a] bg-[#0d0d0d] flex items-center justify-between px-4 lg:px-8" data-testid="top-bar">
          <div className="flex items-center gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] flex items-center gap-2 flex-wrap">
                <span>Dispatcher Console</span>
                {user?.company_name && (
                  <>
                    <span className="text-[#2a2a2a]">·</span>
                    <span className="text-[#4682b4] font-bold tracking-[0.15em]" data-testid="company-name">{user.company_name}</span>
                  </>
                )}
                {billing && billing.subscription_status === "trial" && billing.trial_ends_at && (
                  <NavLink
                    to="/billing"
                    className="px-2 py-0.5 bg-[#4682b4]/15 border border-[#4682b4] text-[#4682b4] font-bold tracking-[0.15em] hover:bg-[#4682b4] hover:text-white"
                    data-testid="trial-pill"
                  >
                    Trial · {billing.days_remaining}d left
                  </NavLink>
                )}
                {billing && billing.subscription_status === "trial_expired" && (
                  <NavLink
                    to="/pricing"
                    className="px-2 py-0.5 bg-[#ef4444]/15 border border-[#ef4444] text-[#ef4444] font-bold tracking-[0.15em] hover:bg-[#ef4444] hover:text-white"
                    data-testid="trial-expired-pill"
                  >
                    Trial expired · upgrade
                  </NavLink>
                )}
                {billing && billing.subscription_status === "active" && (
                  <NavLink
                    to="/billing"
                    className="px-2 py-0.5 bg-[#22c55e]/15 border border-[#22c55e] text-[#22c55e] font-bold tracking-[0.15em] hover:bg-[#22c55e] hover:text-black"
                    data-testid="active-plan-pill"
                  >
                    {billing.subscription_plan?.toUpperCase()} · {billing.days_remaining}d
                  </NavLink>
                )}
              </div>
              <h1 className="font-heading font-black text-xl tracking-tight">{pageTitle}</h1>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            {user?.role === "super_admin" && (
              <CompanySwitcher user={user} />
            )}
            <KpiPill label="Active" value={kpis.active_assets} accent="#22c55e" testId="kpi-active" />
            <KpiPill label="Jobs" value={kpis.scheduled_jobs} accent="#4682b4" testId="kpi-jobs" />
            <KpiPill
              label="Alerts"
              value={kpis.compliance_alerts}
              accent={kpis.compliance_alerts > 0 ? "#ef4444" : "#71717a"}
              pulse={kpis.compliance_alerts > 0}
              testId="kpi-alerts"
            />
            {(kpis.overdue_service > 0 || kpis.due_soon_service > 0) && (
              <NavLink to="/assets" data-testid="kpi-service-link">
                <KpiPill
                  label="Service"
                  value={kpis.overdue_service + kpis.due_soon_service}
                  accent={kpis.overdue_service > 0 ? "#ef4444" : "#ff8c00"}
                  pulse={kpis.overdue_service > 0}
                  testId="kpi-service"
                />
              </NavLink>
            )}

            <div className="h-8 w-px bg-[#2a2a2a] mx-2" />

            <PushToggle />

            <button
              onClick={() => setSettingsOpen(true)}
              className="w-10 h-10 border border-[#2a2a2a] hover:border-[#ff8c00] hover:text-[#ff8c00] flex items-center justify-center transition-colors"
              data-testid="settings-btn"
              title="Settings"
            >
              <Gear size={16} />
            </button>

            <button
              className="w-10 h-10 border border-[#2a2a2a] hover:border-[#ff8c00] flex items-center justify-center transition-colors"
              data-testid="notifications-btn"
            >
              <Bell size={18} />
              {kpis.alert_assets > 0 && (
                <span className="absolute mt-[-12px] ml-[12px] w-2 h-2 bg-[#ff8c00] rounded-full ll-status-blink"></span>
              )}
            </button>

            <div className="flex items-center gap-3 ml-2 pl-3 border-l border-[#2a2a2a]">
              <div className="text-right hidden lg:block">
                <div className="text-xs font-semibold leading-tight" data-testid="user-name">{user?.name || "Dispatcher"}</div>
                <div className="text-[10px] text-[#71717a] uppercase tracking-wider">{user?.role === "admin" ? "Lead Dispatcher" : "Dispatcher"}</div>
              </div>
              <div className="w-10 h-10 bg-[#333] border border-[#4d4d4d] overflow-hidden flex items-center justify-center font-mono font-bold text-sm">
                {user?.name ? user.name.split(" ").map(n => n[0]).join("").slice(0,2).toUpperCase() : "DR"}
              </div>
              <button
                onClick={async () => { await logout(); navigate("/login"); }}
                className="w-10 h-10 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] flex items-center justify-center transition-colors"
                data-testid="logout-btn"
                title="Sign out"
              >
                <SignOut size={16} />
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-hidden">
          {user?.is_viewing_other && (
            <div className="bg-[#ff8c00] text-black px-4 py-1.5 flex items-center justify-center gap-3 text-[11px] uppercase tracking-[0.2em] font-bold border-b border-[#ff8c00]/60" data-testid="viewing-banner">
              <Crown size={12} weight="fill" />
              <span>Super-admin · viewing <strong>{user.company_name}</strong> (home: {user.real_company_name})</span>
            </div>
          )}
          <Outlet />
        </main>
      </div>
      {settingsOpen && <SettingsDialog onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

function KpiPill({ label, value, accent, pulse = false, testId }) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-2 border border-[#2a2a2a] bg-[#0d0d0d]"
      data-testid={testId}
    >
      <span
        className={`w-2 h-2 rounded-full ${pulse ? "ll-status-blink" : ""}`}
        style={{ background: accent }}
      ></span>
      <div className="flex items-baseline gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">{label}</span>
        <span className="font-mono font-bold text-lg" style={{ color: accent }}>
          {String(value).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}
